import type { AxiosInstance } from 'axios';
import type {
  CreateCustomerBody,
  CreateCustomerResponse,
  DeleteCustomerResponse,
  GetCustomerResponse,
  ListCustomersResponse,
  UpdateCustomerBody,
  UpdateCustomerResponse,
} from '../types/customers';
import type {
  OrderDoc,
  PaginatedOrders,
} from '../redux/backoffice/ordersSlice';

/** Server accepts uppercase; None means "all statuses". */
export type CustomerOrdersStatus = 'COMPLETED' | 'PENDING' | 'PARTIAL';

type AuthShape = { accessToken?: string | null };

const withAuth = (auth: AuthShape) => ({
  headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
});

const qs = (locationId: string, extra: Record<string, string | number> = {}) =>
  new URLSearchParams({
    location_id: locationId,
    ...Object.fromEntries(
      Object.entries(extra).map(([k, v]) => [k, String(v)]),
    ),
  }).toString();

const clampLimit = (limit: number) => Math.min(100, Math.max(1, Number(limit) || 50));
const clampPage = (page: number) => Math.max(1, Number(page) || 1);

const toArray = (value: any): any[] => (Array.isArray(value) ? value : []);

export const parseCustomersResponse = (response: any) => {
  const res = response?.data;
  if (!res) return null;

  if (res.success === true && Array.isArray(res.customers)) {
    return {
      success: true,
      customers: res.customers,
      total: Number(res.total ?? res.customers.length),
      page: Number(res.page ?? 1),
      limit: Number(res.limit ?? 50),
      message: String(res.message || ''),
    };
  }

  if (res.status === 200 && res.data) {
    const d = res.data;
    const customers = Array.isArray(d.customers)
      ? d.customers
      : toArray(d.docs ?? d.data);
    return {
      success: true,
      customers,
      total: Number(d.total ?? d.totalDocs ?? customers.length),
      page: Number(d.page ?? d.currentPage ?? 1),
      limit: Number(d.limit ?? d.perPage ?? 50),
      message: String(res.message || ''),
    };
  }

  return null;
};

export const parseCustomerResponse = (response: any) => {
  const res = response?.data;
  if (!res) return null;

  if (res.success === true && res.customer) {
    return {
      success: true,
      customer: res.customer,
      message: String(res.message || ''),
    };
  }

  if (res.status === 200 && res.data) {
    return {
      success: true,
      customer: res.data.customer ?? res.data,
      message: String(res.message || ''),
    };
  }

  return null;
};

const listCustomersCore = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  page: number,
  limit: number,
  auth: AuthShape,
  q?: string,
) => {
  const searchQ = String(q || '').trim();
  return axiosinstance.get<ListCustomersResponse>(
    `/bp_get_customers_information?${qs(locationId, {
      page: clampPage(page),
      limit: clampLimit(limit),
      ...(searchQ ? { q: searchQ } : {}),
    })}`,
    withAuth(auth),
  );
};

const getCustomerCore = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  customerId: string,
  auth: AuthShape,
) => {
  return axiosinstance.get<GetCustomerResponse>(
    `/bp_get_customer_information/${customerId}?${qs(locationId)}`,
    withAuth(auth),
  );
};

const createCustomerCore = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  payload: CreateCustomerBody,
  auth: AuthShape,
) => {
  return axiosinstance.post<CreateCustomerResponse>(
    `/bp_add_customer_information?${qs(locationId)}`,
    {
      bp_customer_name: payload.bp_customer_name.trim(),
      bp_customer_phone_number: payload.bp_customer_phone_number.trim(),
      ...(payload.bp_customer_email_address
        ? {
            bp_customer_email_address: payload.bp_customer_email_address.trim(),
          }
        : {}),
    },
    withAuth(auth),
  );
};

const updateCustomerCore = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  customerId: string,
  payload: UpdateCustomerBody,
  auth: AuthShape,
) => {
  const body: UpdateCustomerBody = {};
  if (typeof payload.bp_customer_name === 'string') {
    body.bp_customer_name = payload.bp_customer_name.trim();
  }
  if (typeof payload.bp_customer_phone_number === 'string') {
    body.bp_customer_phone_number = payload.bp_customer_phone_number.trim();
  }
  if (typeof payload.bp_customer_email_address === 'string') {
    body.bp_customer_email_address = payload.bp_customer_email_address.trim();
  }
  return axiosinstance.patch<UpdateCustomerResponse>(
    `/bp_update_customer_information/${customerId}?${qs(locationId)}`,
    body,
    withAuth(auth),
  );
};

const deleteCustomerCore = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  customerId: string,
  auth: AuthShape,
) => {
  return axiosinstance.delete<DeleteCustomerResponse>(
    `/bp_delete_customer_information/${customerId}?${qs(locationId)}`,
    withAuth(auth),
  );
};

export const listCustomers = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  page: number,
  limit: number,
  auth: AuthShape,
  q?: string,
) => {
  try {
    return await listCustomersCore(
      axiosinstance,
      locationId,
      page,
      limit,
      auth,
      q,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getCustomer = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  customerId: string,
  auth: AuthShape,
) => {
  try {
    return await getCustomerCore(axiosinstance, locationId, customerId, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const createCustomer = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  payload: CreateCustomerBody,
  auth: AuthShape,
) => {
  try {
    return await createCustomerCore(axiosinstance, locationId, payload, auth);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const updateCustomer = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  customerId: string,
  payload: UpdateCustomerBody,
  auth: AuthShape,
) => {
  try {
    return await updateCustomerCore(
      axiosinstance,
      locationId,
      customerId,
      payload,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

/** Normalize `/bp_get_customer_orders/:customerId` into the same shape the
 * backoffice orders slice already understands (see BackOfficeCompletedOrders
 * `normalizePaginated`). Accepts either flat payloads or `{ data: {...} }`. */
export const parseCustomerOrdersResponse = (
  response: any,
): PaginatedOrders | null => {
  const res = response?.data;
  if (!res) return null;

  const payload = res?.data && typeof res.data === 'object' ? res.data : res;

  const docs = Array.isArray(payload?.docs)
    ? (payload.docs as OrderDoc[])
    : Array.isArray(payload?.data)
      ? (payload.data as OrderDoc[])
      : [];

  const limit = Number(payload?.limit ?? payload?.perPage ?? 50);
  const perPage = Number(payload?.perPage ?? payload?.limit ?? limit);
  const page = Number(payload?.page ?? payload?.currentPage ?? 1);
  const totalDocs = Number(payload?.totalDocs ?? payload?.total ?? docs.length);
  const totalPages = Number(
    payload?.totalPages ?? Math.max(1, Math.ceil(totalDocs / Math.max(perPage, 1))),
  );
  const hasNextPage = Boolean(payload?.hasNextPage ?? page < totalPages);
  const hasPrevPage = Boolean(payload?.hasPrevPage ?? page > 1);
  const nextPage = Number(
    payload?.nextPage ?? (hasNextPage ? page + 1 : page),
  );

  return {
    docs,
    hasNextPage,
    hasPrevPage,
    limit,
    nextPage,
    page,
    perPage,
    totalDocs,
    totalPages,
  };
};

const getCustomerOrdersCore = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  customerId: string,
  page: number,
  limit: number,
  status: CustomerOrdersStatus | null,
  auth: AuthShape,
) => {
  const query = qs(locationId, {
    page: clampPage(page),
    limit: clampLimit(limit),
    ...(status ? { status } : {}),
  });
  return axiosinstance.get(
    `/bp_get_customer_orders/${encodeURIComponent(customerId)}?${query}`,
    withAuth(auth),
  );
};

export const getCustomerOrders = async (
  axiosinstance: AxiosInstance,
  args: {
    locationId: string;
    customerId: string;
    page?: number;
    limit?: number;
    status?: CustomerOrdersStatus | null;
  },
  auth: AuthShape,
) => {
  try {
    return await getCustomerOrdersCore(
      axiosinstance,
      args.locationId,
      args.customerId,
      Number(args.page ?? 1),
      Number(args.limit ?? 50),
      args.status ?? null,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};

export const deleteCustomer = async (
  axiosinstance: AxiosInstance,
  locationId: string,
  customerId: string,
  auth: AuthShape,
) => {
  try {
    return await deleteCustomerCore(
      axiosinstance,
      locationId,
      customerId,
      auth,
    );
  } catch (e) {
    return Promise.reject(e);
  }
};
